import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { House, Buildings, Storefront, ArrowRight, ArrowLeft, Check, Globe, MapPin, CurrencyCircleDollar, GoogleLogo } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { LANGUAGES, t } from '@/i18n/translations';

const AVAILABLE_MODULES = [
  { id: "advanced_stock", pilar: 1, name: "Estoque Avançado", desc: "Lotes, validade, multi-almoxarifado, fornecedores" },
  { id: "advanced_production", pilar: 2, name: "Produção Complexa", desc: "Sub-receitas, ordens de produção, desperdício" },
  { id: "financial_intelligence", pilar: 3, name: "Inteligência Financeira", desc: "CMV em tempo real, DRE, histórico de preços" },
  { id: "rbac", pilar: 4, name: "Multi-usuário e Permissões", desc: "Equipe, permissões, auditoria" },
  { id: "supply_chain", pilar: 5, name: "Logística de Suprimentos", desc: "Fornecedores, aprovação, recebimento" },
  { id: "menu_intelligence", pilar: 6, name: "Cardápio e Vendas", desc: "Engenharia de cardápio, PDV/iFood" },
  { id: "business_intelligence", pilar: 7, name: "BI e Relatórios", desc: "Dashboards gerenciais, alertas de custos" },
  { id: "menu_builder", pilar: 8, name: "Cardápios e Menus", desc: "Montagem profissional, link público, WhatsApp" },
  { id: "checklists", pilar: 9, name: "Checklists Operacionais", desc: "Abertura, fechamento, produção, templates" },
];

// Modules recommended per business segment
const SEGMENT_MODULES = {
  restaurante:  ["advanced_stock", "advanced_production", "financial_intelligence", "rbac", "menu_intelligence", "checklists"],
  confeitaria:  ["advanced_stock", "advanced_production", "financial_intelligence", "supply_chain", "checklists"],
  eventos:      ["advanced_production", "financial_intelligence", "rbac", "supply_chain", "checklists", "business_intelligence"],
  delivery:     ["advanced_stock", "financial_intelligence", "menu_intelligence", "menu_builder", "checklists"],
  hotelaria:    ["advanced_stock", "advanced_production", "financial_intelligence", "rbac", "supply_chain", "menu_intelligence", "business_intelligence", "checklists"],
  autonomo:     ["financial_intelligence", "checklists"],
  food_truck:   ["advanced_stock", "financial_intelligence", "menu_builder", "checklists"],
  escola:       ["advanced_production", "checklists"],
  industria:    ["advanced_stock", "advanced_production", "financial_intelligence", "rbac", "supply_chain", "business_intelligence", "checklists"],
  outro:        ["financial_intelligence", "checklists"],
};

const CURRENCIES = [
  { code: "BRL", symbol: "R$", name: "Real Brasileiro", flag: "🇧🇷" },
  { code: "USD", symbol: "$", name: "US Dollar", flag: "🇺🇸" },
  { code: "EUR", symbol: "€", name: "Euro", flag: "🇪🇺" },
  { code: "GBP", symbol: "£", name: "British Pound", flag: "🇬🇧" },
  { code: "ARS", symbol: "$", name: "Peso Argentino", flag: "🇦🇷" },
  { code: "MXN", symbol: "$", name: "Peso Mexicano", flag: "🇲🇽" },
  { code: "CLP", symbol: "$", name: "Peso Chileno", flag: "🇨🇱" },
  { code: "COP", symbol: "$", name: "Peso Colombiano", flag: "🇨🇴" },
  { code: "PEN", symbol: "S/", name: "Sol Peruano", flag: "🇵🇪" },
  { code: "UYU", symbol: "$", name: "Peso Uruguayo", flag: "🇺🇾" },
];

export default function Register() {
  const [step, setStep] = useState(1); // 1=dados, 2=localização, 3=perfil, 4=modulos
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [language, setLanguage] = useState('pt-BR');
  const [country, setCountry] = useState('');
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [currency, setCurrency] = useState('BRL');
  const [businessType, setBusinessType] = useState('');
  const [accountType, setAccountType] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [businessSegment, setBusinessSegment] = useState('');
  const [enabledModules, setEnabledModules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { register } = useAuth();
  const navigate = useNavigate();

  const selectAccountType = (type) => {
    setAccountType(type);
    const bt = type === 'chef_autonomo' ? 'autonomo' : 'empresa';
    setBusinessType(bt);
    if (type === 'empresa') {
      setEnabledModules(AVAILABLE_MODULES.map((m) => m.id));
    } else {
      const suggested = SEGMENT_MODULES[businessSegment] || [];
      setEnabledModules(suggested);
    }
  };

  const selectSegment = (segId) => {
    setBusinessSegment(segId);
    // Auto-suggest modules based on segment (only if not "empresa" which gets all)
    if (businessType !== 'empresa') {
      const suggested = SEGMENT_MODULES[segId] || [];
      setEnabledModules(suggested);
    }
  };

  const toggleModule = (moduleId) => {
    setEnabledModules((prev) =>
      prev.includes(moduleId) ? prev.filter((m) => m !== moduleId) : [...prev, moduleId]
    );
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      await register(email, password, name, businessType, businessName, enabledModules, language, country, state, city, currency, businessSegment, accountType);
      localStorage.setItem('sop_raft_lang', language);
      toast.success(t('register_success', language) || 'Cadastro realizado com sucesso!');
      navigate('/');
    } catch (err) {
      const detail = err.response?.data?.detail;
      let message = 'Erro ao cadastrar. Tente novamente.';
      if (typeof detail === 'string') message = detail;
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const canAdvanceStep1 = name.trim() && email.trim() && password.length >= 6;
  const canAdvanceStep2 = currency;
  const canAdvanceStep3 = !!businessType;
  const totalSteps = 4;

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 px-4 py-8">
      <div className="w-full max-w-lg">
        <div className="text-center mb-6">
          <h1 className="text-3xl font-extrabold tracking-tight text-zinc-950">SOP & RAFT</h1>
          <p className="text-[10px] tracking-[0.15em] uppercase text-zinc-400 mt-1 font-semibold">Cozinha Inteligente</p>
        </div>

        {/* Progress */}
        <div className="flex items-center justify-center gap-2 mb-6">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                step >= s ? 'bg-zinc-950 text-white' : 'bg-zinc-200 text-zinc-400'
              }`}>
                {step > s ? <Check size={14} weight="bold" /> : s}
              </div>
              {s < totalSteps && <div className={`w-8 h-0.5 ${step > s ? 'bg-zinc-950' : 'bg-zinc-200'}`} />}
            </div>
          ))}
        </div>

        <div className="bg-white rounded-sm border border-zinc-200 shadow-sm p-8">
          {/* Step 1: Basic Data */}
          {step === 1 && (
            <>
              <h2 className="text-xl font-bold text-zinc-950 mb-1">{t('create_account', language)}</h2>
              <p className="text-sm text-zinc-500 mb-6">{t('your_access_data', language)}</p>
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-sm mb-4" data-testid="register-error">
                  <p className="text-sm text-red-700 font-medium">{error}</p>
                </div>
              )}
              <div className="space-y-4">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700 flex items-center gap-1.5">
                    <Globe size={14} /> {t('language', language)}
                  </Label>
                  <div className="flex gap-2 mt-1.5">
                    {LANGUAGES.map((lng) => (
                      <button key={lng.code} type="button" onClick={() => setLanguage(lng.code)} data-testid={`lang-${lng.code}`}
                        className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg border-2 text-sm font-medium transition-all ${
                          language === lng.code ? 'border-zinc-950 bg-zinc-50 text-zinc-900' : 'border-zinc-200 text-zinc-500 hover:border-zinc-300'
                        }`}>
                        <span className="text-lg">{lng.flag}</span>
                        <span className="text-xs">{lng.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">{t('name', language)}</Label>
                  <Input type="text" value={name} onChange={(e) => setName(e.target.value)}
                    required placeholder={language === 'en' ? "Your name" : language === 'es' ? "Tu nombre" : "Seu nome"} className="mt-1.5" data-testid="register-name" />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">{t('email', language)}</Label>
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    required placeholder={language === 'en' ? "your@email.com" : language === 'es' ? "tu@correo.com" : "seu@email.com"} className="mt-1.5" data-testid="register-email" />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">{t('password', language)}</Label>
                  <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                    required placeholder={t('min_6_chars', language)} minLength={6} className="mt-1.5" data-testid="register-password" />
                </div>
                <Button onClick={() => setStep(2)} disabled={!canAdvanceStep1}
                  className="w-full bg-zinc-950 text-white hover:bg-zinc-800" data-testid="register-next-step2">
                  {t('next', language)} <ArrowRight size={16} className="ml-2" />
                </Button>
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-zinc-200" />
                  <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">ou</span>
                  <div className="flex-1 h-px bg-zinc-200" />
                </div>
                <button type="button" onClick={() => { window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + '/')}`; }}
                  data-testid="google-register-btn"
                  className="w-full flex items-center justify-center gap-2 border border-zinc-300 rounded-sm py-2.5 min-h-[44px] text-sm font-semibold text-zinc-700 hover:bg-zinc-50 transition-colors">
                  <GoogleLogo size={18} weight="bold" /> Criar conta com Google
                </button>
                <div className="text-center text-sm text-zinc-600">
                  {t('already_have_account', language)}{' '}
                  <Link to="/login" className="text-zinc-950 font-semibold hover:underline">{t('login_link', language)}</Link>
                </div>
              </div>
            </>
          )}

          {/* Step 2: Location & Currency */}
          {step === 2 && (
            <>
              <h2 className="text-xl font-bold text-zinc-950 mb-1 flex items-center gap-2">
                <MapPin size={22} weight="bold" />
                {language === 'en' ? 'Your Location' : language === 'es' ? 'Tu Ubicación' : 'Sua Localização'}
              </h2>
              <p className="text-sm text-zinc-500 mb-6">
                {language === 'en' ? 'Tell us where you are to customize your experience' : language === 'es' ? 'Dinos dónde estás para personalizar tu experiencia' : 'Nos diga onde você está para personalizar sua experiência'}
              </p>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">
                    {language === 'en' ? 'Country' : language === 'es' ? 'País' : 'País'}
                  </Label>
                  <Input value={country} onChange={(e) => setCountry(e.target.value)}
                    placeholder={language === 'en' ? "e.g. Brazil" : language === 'es' ? "ej. Argentina" : "ex. Brasil"} className="mt-1.5" data-testid="register-country" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">
                      {language === 'en' ? 'State/Province' : language === 'es' ? 'Estado/Provincia' : 'Estado'}
                    </Label>
                    <Input value={state} onChange={(e) => setState(e.target.value)}
                      placeholder={language === 'en' ? "e.g. California" : language === 'es' ? "ej. Buenos Aires" : "ex. São Paulo"} className="mt-1.5" data-testid="register-state" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">
                      {language === 'en' ? 'City' : language === 'es' ? 'Ciudad' : 'Cidade'}
                    </Label>
                    <Input value={city} onChange={(e) => setCity(e.target.value)}
                      placeholder={language === 'en' ? "e.g. San Francisco" : language === 'es' ? "ej. Buenos Aires" : "ex. São Paulo"} className="mt-1.5" data-testid="register-city" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700 flex items-center gap-1.5">
                    <CurrencyCircleDollar size={14} />
                    {language === 'en' ? 'Currency' : language === 'es' ? 'Moneda' : 'Moeda'}
                  </Label>
                  <div className="grid grid-cols-2 gap-2 mt-1.5 max-h-48 overflow-y-auto">
                    {CURRENCIES.map((cur) => (
                      <button key={cur.code} type="button" onClick={() => setCurrency(cur.code)} data-testid={`currency-${cur.code}`}
                        className={`flex items-center gap-2 p-2.5 rounded-lg border-2 text-left transition-all ${
                          currency === cur.code ? 'border-zinc-950 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-300'
                        }`}>
                        <span className="text-lg">{cur.flag}</span>
                        <div>
                          <p className="text-xs font-bold text-zinc-900">{cur.code}</p>
                          <p className="text-[10px] text-zinc-500">{cur.symbol} · {cur.name}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex gap-3 mt-2">
                  <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
                    <ArrowLeft size={16} className="mr-2" /> {t('back', language)}
                  </Button>
                  <Button onClick={() => setStep(3)} disabled={!canAdvanceStep2}
                    className="flex-1 bg-zinc-950 text-white hover:bg-zinc-800" data-testid="register-next-step3">
                    {t('next', language)} <ArrowRight size={16} className="ml-2" />
                  </Button>
                </div>
              </div>
            </>
          )}

          {/* Step 3: Business Type */}
          {step === 3 && (
            <>
              <h2 className="text-xl font-bold text-zinc-950 mb-1">{t('business_profile', language)}</h2>
              <p className="text-sm text-zinc-500 mb-4">{t('how_identify', language)}</p>
              <div className="space-y-3 mb-4">
                <button onClick={() => selectAccountType('chef_autonomo')} data-testid="select-autonomo"
                  className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                    accountType === 'chef_autonomo' ? 'border-zinc-950 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-300'
                  }`}>
                  <div className="flex items-center gap-3">
                    <House size={22} weight={accountType === 'chef_autonomo' ? 'fill' : 'regular'} className="text-zinc-700" />
                    <div>
                      <span className="text-base font-bold text-zinc-900">Chef Autônomo / Produção Caseira</span>
                      <p className="text-xs text-zinc-500">{t('autonomo_desc', language)}</p>
                    </div>
                  </div>
                </button>
                <button onClick={() => selectAccountType('microempreendimento')} data-testid="select-micro"
                  className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                    accountType === 'microempreendimento' ? 'border-zinc-950 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-300'
                  }`}>
                  <div className="flex items-center gap-3">
                    <Storefront size={22} weight={accountType === 'microempreendimento' ? 'fill' : 'regular'} className="text-zinc-700" />
                    <div>
                      <span className="text-base font-bold text-zinc-900">Microempreendimento</span>
                      <p className="text-xs text-zinc-500">Pequenos negócios de cozinha (MEI, ateliês, dark kitchens)</p>
                    </div>
                  </div>
                </button>
                <button onClick={() => selectAccountType('empresa')} data-testid="select-empresa"
                  className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                    accountType === 'empresa' ? 'border-zinc-950 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-300'
                  }`}>
                  <div className="flex items-center gap-3">
                    <Buildings size={22} weight={accountType === 'empresa' ? 'fill' : 'regular'} className="text-zinc-700" />
                    <div>
                      <span className="text-base font-bold text-zinc-900">{t('empresa', language)}</span>
                      <p className="text-xs text-zinc-500">{t('empresa_desc', language)}</p>
                    </div>
                  </div>
                </button>
              </div>

              {/* Business Segment */}
              <div className="mb-4">
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Segmento / Ramo de Atuação</Label>
                <div className="grid grid-cols-2 gap-1.5 mt-1.5">
                  {[
                    { id: "restaurante", label: "Restaurante / Bar", emoji: "🍽️" },
                    { id: "confeitaria", label: "Confeitaria / Padaria", emoji: "🎂" },
                    { id: "eventos", label: "Eventos / Buffet", emoji: "🎪" },
                    { id: "delivery", label: "Delivery / Dark Kitchen", emoji: "🍕" },
                    { id: "hotelaria", label: "Hotelaria", emoji: "🏨" },
                    { id: "autonomo", label: "Chef Autônomo", emoji: "🧑‍🍳" },
                    { id: "food_truck", label: "Food Truck", emoji: "🚚" },
                    { id: "escola", label: "Escola / Ensino", emoji: "📚" },
                    { id: "industria", label: "Indústria", emoji: "🏭" },
                    { id: "outro", label: "Outro", emoji: "➕" },
                  ].map(seg => (
                    <button key={seg.id} onClick={() => selectSegment(seg.id)} data-testid={`segment-${seg.id}`}
                      className={`p-2.5 rounded-lg border text-left text-sm transition-all flex items-center gap-2 ${
                        businessSegment === seg.id ? 'border-zinc-950 bg-zinc-50 font-semibold' : 'border-zinc-200 hover:border-zinc-300'
                      }`}>
                      <span className="text-base">{seg.emoji}</span>
                      <span className="text-zinc-800 text-xs">{seg.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">{t('business_name', language)} ({t('optional', language)})</Label>
                <Input value={businessName} onChange={(e) => setBusinessName(e.target.value)}
                  placeholder={t('business_name_placeholder', language)} className="mt-1.5" data-testid="register-business-name" />
              </div>
              <div className="flex gap-3 mt-6">
                <Button variant="outline" onClick={() => setStep(2)} className="flex-1">
                  <ArrowLeft size={16} className="mr-2" /> {t('back', language)}
                </Button>
                <Button onClick={() => setStep(4)} disabled={!canAdvanceStep3}
                  className="flex-1 bg-zinc-950 text-white hover:bg-zinc-800" data-testid="register-next-step4">
                  {t('next', language)} <ArrowRight size={16} className="ml-2" />
                </Button>
              </div>
            </>
          )}

          {/* Step 4: Module Selection */}
          {step === 4 && (
            <>
              <h2 className="text-xl font-bold text-zinc-950 mb-1">{t('advanced_modules', language)}</h2>
              <p className="text-sm text-zinc-500 mb-4">
                {businessType === 'empresa' ? t('all_preselected', language) 
                  : businessSegment && SEGMENT_MODULES[businessSegment]
                    ? `Módulos sugeridos para ${businessSegment === 'eventos' ? 'Eventos/Buffet' : businessSegment === 'confeitaria' ? 'Confeitaria/Padaria' : businessSegment}. Ajuste como quiser.`
                    : t('select_extras', language)}
              </p>
              <div className="space-y-2 max-h-72 overflow-y-auto mb-6">
                {AVAILABLE_MODULES.map((mod) => {
                  const isSuggested = (SEGMENT_MODULES[businessSegment] || []).includes(mod.id);
                  return (
                    <div key={mod.id} data-testid={`module-select-${mod.id}`}
                      className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                        enabledModules.includes(mod.id) ? 'border-zinc-950 bg-zinc-50' : 'border-zinc-200'
                      }`}>
                      <Checkbox checked={enabledModules.includes(mod.id)} onCheckedChange={() => toggleModule(mod.id)} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-zinc-900 flex items-center gap-1.5">
                          <span className="text-xs text-zinc-400">P{mod.pilar}</span>{mod.name}
                          {isSuggested && <span className="text-[9px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider">Sugerido</span>}
                        </p>
                        <p className="text-xs text-zinc-500 truncate">{mod.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(3)} className="flex-1">
                  <ArrowLeft size={16} className="mr-2" /> {t('back', language)}
                </Button>
                <Button onClick={handleSubmit} disabled={loading}
                  className="flex-1 bg-zinc-950 text-white hover:bg-zinc-800" data-testid="register-submit">
                  {loading ? t('creating', language) : t('create_account', language)}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
